<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div :class="['navbar-item', 'search', 'displayColumn', displayResults ? 'is-active' : '']" v-click-outside="deactivate">
  <b-field grouped>
    <b-radio v-model="searchType" native-value="case">Case</b-radio>
    <b-radio v-model="searchType" native-value="patient">Patient</b-radio>
  </b-field>
  <b-field class="no-margin" :type="error ? 'is-danger' : null">
    <b-input class="global-search" v-model="searchString" :placeholder="$t('search-placeholder')" type="search" icon="search" @click.native="activate()" :loading="loading" :disabled="error" />
    <p class="control">
      <router-link class="button" to="/advanced-search" active-class="router-link-active" @click.native="deactivate">+</router-link>
    </p>
  </b-field>

  <div class="navbar-dropdown search-results" v-show="true">
    <p class="resultColumn">
      <span style="flex: 1;">{{$t('case')}} ({{cases.length}})</span>
      <span style="flex: 1;">{{$t('patient-his-id')}}</span>
    </p>
    <div v-if="cases.length > 0">
      <router-link v-for="project in subsetProjects" :key="project.id" :to="`/worklist/status-${project.status}/case/${project.id}?tab=images`" class="navbar-item" @click.native="deactivate" >
        <p class="resultRow" >
          <span
            v-html="highlightedCaseName(project.name)"
            class="half"
          ></span>
          <span
            v-html="project.patientId ? highlightedPatientId(project.patientId) : 'No Data'"
            class="half"
            :class="{ noData: !project.patientId }"
          ></span>
        </p>
      </router-link>
      <a v-if="moreProjects" class="navbar-item">...</a>
    </div>
    <span v-else class="navbar-item no-result">{{$t('no-project')}}</span>

    <!-- <h2>{{$t('images')}} ({{imageDatas.length}})</h2>
    <p v-if="imageDatas.length > 0">
      <router-link v-for="img in subsetImages" :key="img.id" :to="`/project/${img.projectId}/image/${img.id}`" class="navbar-item" @click.native="deactivate" >
      <template v-for="field in ['instanceFilename']">
        <template v-if="img[field] && img[field].toLowerCase().includes(searchString.toLowerCase())">
          <div :key="field">
            <span v-html="htmlImageName(img)"></span>
          </div>
        </template>
      </template>
      
      </router-link>
      <a v-if="moreImages" class="navbar-item">...</a>
    </p>
    <span v-else class="navbar-item no-result">{{$t('no-image')}}</span> -->

    <div v-if="moreImages || moreProjects" class="search-view-all">
      <router-link class="button is-small" :to="`/advanced-search/${searchString}`" @click.native="deactivate">
        {{$t('button-view-all')}} ({{totalNbResults}})
      </router-link>
    </div>
  </div>
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';
import {ImageInstanceCollection} from 'cytomine-client';
import {getWildcardRegexp} from '@/utils/string-utils';

export default {
  name: 'cytomine-searcher',
  data() {
    return {
      isActive: false,
      loading: false,
      error: false,
      searchString: '',
      maxNbDisplayed: 25,
      
      debounceTimer: null,
      cases: [],
      imageDatas: [],
      
      searchType: 'case',
    };
  },
  watch: {
    searchString() {
      this.executeSearch();
    },
    searchType() {
      this.executeSearch();
    }
  },
  computed: {
    currentUser: get('currentUser/user'),
    displayResults() {
      return this.isActive && !this.error && this.searchString.length > 0;
    },
    regexp() {
      return getWildcardRegexp(this.searchString);
    },
    subsetProjects() {
      return this.cases;
      // return this.cases.slice(0, this.maxNbDisplayed);
    },
    moreProjects() {
      return this.cases > this.subsetProjects;
    },
    subsetImages() {
      return this.imageDatas.slice(0, this.maxNbDisplayed);
    },
    moreImages() {
      return this.imageDatas > this.subsetImages;
    },
    totalNbResults() {
      return this.imageDatas.length + this.cases.length;
    }
  },
  methods: {
    async executeSearch() {
      if (this.searchString.length === 0) {
        this.cases = [];
        // this.imageDatas = [];
        return;
      }

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(async () => {
        try {
          let data = await ImageInstanceCollection.fetchGlobalSearch({
            type: this.searchType,
            keyword: this.searchString
          });
          this.cases = data.case;
          // this.imageDatas = data.image;
        }
        catch (error) {
          console.error('Search API error:', error);
        }
      }, 300);
    },
    async activate() {
      if(!this.isActive) {
        try {
          this.loading = true;
        }
        catch(error) {
          console.log(error);
          this.error = true;
        }
        this.loading = false;
        this.isActive = true;
      }
    },
    deactivate() {
      this.isActive = false;
    },
    imageName(image) {
      return String(image.blindedName || image.instanceFilename);
    },
    highlightedName(value) {
      return this.truncateText(value)
        .replace(this.regexp, '<strong>$1</strong>');
    },
    highlightedCaseName(value) {
      if (this.searchType === 'case')
        return this.highlightedName(value);
      return this.truncateText(value);
    },
    highlightedPatientId(value) {
      if (this.searchType === 'patient')
        return this.highlightedName(value);
      return this.truncateText(value);
    },
    truncateText(value) {
      const maxLength = 15;
      if (!value) return '';
      if (value.length > maxLength)
        return value.substring(0, maxLength) + '...';
      return value;
    },
    htmlImageName(img) {
      let blindIndication = img.blindedName ? `<span class="blind">[${this.$t('blinded-name-indication')}] </span>` : '';
      let inProject = `<span class="in-project">(${this.$t('in-project', {projectName: img.projectName})})</span>`;
      return `${blindIndication}${this.highlightedName(this.imageName(img))}&nbsp;${inProject}`;
    }
  }
};
</script>

<style scoped>
.displayColumn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.resultColumn {
  background: #f1f1f1;
  text-transform: uppercase;
  font-size: 0.9em;
  font-weight: 600;
  border-top: 1px solid #e3e3e3;
  margin-bottom: 0;

  display: flex;
  padding: 0.2em 1em 0.2em 1.75em;
}

.resultRow {
  display: flex;
  width: 100%;
}
.resultRow .half {
  flex: 1;        /* 부모 폭의 절반씩 차지 */
  overflow: hidden; /* 길이가 넘으면 숨김 */
  text-overflow: ellipsis; /* ... 표시 */
  white-space: nowrap;     /* 한 줄로 표시 */
}
.resultRow span.noData {
  font-style: italic;
  color: grey;
}

/* .navbar-item.search {
  height: 100%;
} */

.navbar-dropdown {
  max-height: 70vh;
  overflow: auto;
}

.navbar-item:not(.is-active) .navbar-dropdown { /* display dropdown if inactive even on mobile */
  display: none;
}

/* .navbar-dropdown.search-results h2 {
  background: #f1f1f1;
  text-transform: uppercase;
  font-size: 0.9em;
  font-weight: 600;
  padding: 0.2em 0 0.3em 3em;
  border-top: 1px solid #e3e3e3;
  margin-bottom: 0;
} */

.navbar-dropdown.search-results .navbar-item {
  font-weight: normal;
  padding: 0.2em 1em 0.2em 1.75em;
  white-space: pre;
}

.search-view-all {
  border-top: 1px solid #e3e3e3;
  padding-top: 0.5em;
  text-align: center;
}

.no-result {
  color: grey;
}

>>> .blind {
  font-size: 0.9em;
  text-transform: uppercase;
}

.global-search {
  max-width: 15em;
}

.no-margin {
  margin: 0 !important;
}

>>> .global-search .fas {
  padding-right: 0px !important;
}
</style>
